export const LARGE_SCREEN_SIZE = 600;
export const AXIOS_TIMEOUT = 60000;

export const ADMIN_ROLES = ['super-admin', 'owner', 'admin'];
export const USER_ROLES = ['mechanic', 'user'];

export const LOCAL_STORAGE_KEY = 'local';
export const SERVER_ERR_MSG = 'Server Error!';

export const API_URL = 'https://api.chabaddigital.com'

// Which of the two apps on the API this is. Sent when signing in, so the API
// holds the session to the apps its organization has even where the address
// can't tell them apart (local development runs both on localhost).
export const CLIENT_APP = 'chabad_digital';

// Printed in the footer of the downloadable yahrzeit QR poster. Edit here and
// every generated poster picks it up; a blank field is simply left off the
// image rather than rendered empty.
export const CHABAD_DIGITAL_BRAND = {
  website: 'chabaddigital.com',
  email: 'info@chabaddigital.com',
  phone: '',
  tagline: 'Digital signage & yahrzeit management for the Jewish community',
};

export const STORAGE_URL =
  'https://screenkode-storage.nyc3.digitaloceanspaces.com';
export const ORGANIZATION_LOGO_DIR = 'documents/organization_logos';
export const SCREEN_IMAGES_DIR = 'documents/screen_images'
export const RECENT_PHOTO_IMAGES_DIR = 'documents/recent_photo_images'
export const YURTZEITEN_IMAGES_DIR = 'documents/yurtzeiten_images'
export const EVENT_IMAGES_DIR = 'documents/event_images'
export const INVOICES_DIR = 'documents/invoices';
export const CUSTOM_FONTS_DIR = 'documents/custom_fonts'

// Root of the REST API. Shared by the axios client and by URLs the browser has
// to fetch on its own — a custom font face, for one, is loaded by the font
// engine and never carries the axios instance's headers.
export const API_BASE_URL = `${
  process.env.NODE_ENV === 'development'
    ? process.env.VUE_APP_LOCAL_SERVER_URL
    : process.env.VUE_APP_SERVER_URL
}/${process.env.VUE_APP_VERSION}`;

export const SUBSCRIPTION_PERIOD_MONTHLY = 'monthly';
export const SUBSCRIPTION_PERIOD_YEARLY = 'yearly';

export const REFRESH_SCREEN_SOCKET_EVENT = 'REFRESH_SCREEN_SOCKET_EVENT'
// Emitted with { scheduleId } whenever a switching schedule is saved or removed,
// so an open schedule link picks the change up without waiting for its poll.
export const REFRESH_SCREEN_SCHEDULE_SOCKET_EVENT = 'REFRESH_SCREEN_SCHEDULE_SOCKET_EVENT'

export const DEFAULT_ERROR_MSG = 'Error! Something went wrong.';
export const TOAST_GLOBAL_CONFIG = { position: 'top-right', dismissible: true };

export const TICKET_STATUS_OPEN = 'open'
export const TICKET_STATUS_CLOSED = 'closed'

export const TICKET_COMMENT_TYPES = [
  'ticket-close',
  'ticket-reopen',
  'ticket-comment'
]

// The longest a ticket's subject may be — what the API takes when it is renamed.
export const TICKET_SUBJECT_MAX_LENGTH = 200;

// Files sent with a ticket or a message. Mirrors what the API's ticket routes
// accept before uploading to storage (its utils/ticket_upload), so a bad pick
// is caught here instead of coming back as a 400.
export const TICKET_ATTACHMENTS_DIR = 'documents/ticket_attachments';
export const TICKET_ATTACHMENT_MAX_FILES = 5;
export const TICKET_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const TICKET_ATTACHMENT_EXTENSIONS = [
  '.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic',
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.csv', '.txt', '.rtf',
  '.zip', '.mp4', '.mov', '.webm', '.log', '.json',
];

// How often the app asks what is unread (the badge on Tickets in the navs),
// and how often an open conversation checks for new messages.
export const TICKET_UNREAD_POLL_MS = 30 * 1000;
export const TICKET_THREAD_POLL_MS = 6 * 1000;

export const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

export const DATE_OPTIONS_TRIMMED: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

export const DATETIME_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

export const DAY_NAMES = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שב"ק'];

export const DAYS_IN_HEBREW = [
  "א",
  "ב",
  "ג",
  "ד",
  "ה",
  "ו",
  "ז",
  "ח",
  "ט",
  "י",
  'י"א',
  'י"ב',
  'י"ג',
  'י"ד',
  'ט"ו',
  'ט"ז',
  'י"ז',
  'י"ח',
  'י"ט',
  'כ',
  'כ"א',
  'כ"ב',
  'כ"ג',
  'כ"ד',
  'כ"ה',
  'כ"ו',
  'כ"ז',
  'כ"ח',
  'כ"ט',
  'ל',
  'ל"א'
]

export const HEBREW_MONTHS = [
  "Nisan",
  "Iyyar",
  "Sivan",
  "Tamuz",
  "Av",
  "Elul",
  "Tishrei",
  "Cheshvan",
  "Kislev",
  "Tevet",
  "Sh'vat",
  "Adar",
  "Adar I",
  "Adar II"
]

export const MONTHS_IN_HEBREW = [
  "ניסן",
  "אייר",
  "סיון",
  "תמוז",
  "אב",
  "אלול",
  "תשרי",
  "חשון",
  "כסלו",
  "טבת",
  "שבט",
  "אדר",
  "אדר א",
  "אדר ב"
]


export const ZMANIM_YIDDISH_MAPPING = {
  "chatzotNight": "חצות הלילה",
  "alosHashachar": "עלות השחר",
  "alotHaShachar": "עלות השחר",
  "hanachatTefillin": "הנחת טו''ת",
  "misheyakir": "משיכיר",
  "misheyakirMachmir": "משיכיר (מחמיר)",
  "dawn": "השחר",
  "sunrise": "הנץ החמה",
  "sofZmanShmaMGA19Point8": "סוף זמן קריאת שמע (מגן אברהם 19.8°)",
  "sofZmanShmaMGA16Point1": "סוף זמן קריאת שמע (מגן אברהם 16.1°)",
  "sofZmanShmaMGA": 'סוף זמן קרי"ש (מג"א)',
  "sofZmanShma": 'סוף זמן קרי"ש (גר״א)',
  "sofZmanTfillaMGA19Point8": "סוף זמן תפילה (מגן אברהם 19.8°)",
  "sofZmanTfillaMGA16Point1": "סוף זמן תפילה (מגן אברהם 16.1°)",
  "sofZmanTfillaMGA": 'סוז"ת (מג"א)',
  "sofZmanTfilla": 'סוז"ת (גר״א)',
  "chatzot": "חצות היום",
  "minchaGedola": "מנחה גדולה",
  "minchaGedolaMGA": 'מנחה גדולה (מג"א)',
  "minchaKetana": "מנחה קטנה",
  "minchaKetanaMGA": 'מנחה קטנה (מג"א)',
  "plagHaMincha": "פלג המנחה",
  "plagMinchaMGA90": "פלג המנחה מג''א ",
  "sunset": "שקיעה",
  "beinHaShmashos": "בין השמשות",
  "dusk": "חשכה",
  "tzeit7083deg": "צאת הכוכבים (7.083°)",
  "tzeit85deg": "צאת הכוכבים (8.5°)",
  "tzeit42min": "צאת הכוכבים (42 דקות)",
  "tzeit50min": "צאת הכוכבים (50 דקות)",
  "tzeit72min": "צאת הכוכבים",
  "afterSunset60": "צאת הככובים (60)",
  // Canonical kosher-zmanim keys used by the Zmanim / Zmanim_N pages. Kept
  // alongside the keys above (rather than replacing them) so zmanim records
  // saved with the older keys keep resolving on the display.
  "chatzos": 'חצות היום',
  "minchaGedola30Minutes": 'מנחה גדולה',
  "minchaGedola72Minutes": 'מנחה גדולה (מג"א 72)',
  "minchaKetanaGRAFixedLocalChatzosToSunset": 'מנחה קטנה',
  "minchaKetana72Minutes": 'מנחה קטנה (מג"א 72)',
  "plagHamincha72Minutes": 'פלג המנחה - מג"א',
  "tzais50": "צאת הכוכבים - נ'",
  "tzais60": "צאת הכוכבים - ס'",
  "tzais72": 'צאת הכוכבים - ר"ת',
  "shabbatHadlukasHaneiros": 'הדלקת נרות',
  "shabbatMotzei": 'מוצאי',
  "alos72": 'עלות השחר - קבוע',
  "alos90": "עלות השחר - צ'",
  "alos72Zmanis": 'עלות השחר - זמניות',
  "misheyakir10Point2Degrees": 'משיכיר',
  "misheyakir22Min": 'משיכיר 22 דק׳',
  "sofZmanShmaMGA72Minutes": 'סוף זמן ק"ש - מג"א',
  "sofZmanShmaMGA90Minutes": 'סוף זמן ק"ש - מג"א צ\'',
  "sofZmanShmaGRA": 'סוף זמן ק"ש - גר"א',
  "sofZmanTfilaMGA72Minutes": 'סוף זמן תפילה - מג"א',
  "sofZmanTfilaGRA": 'סוף זמן תפילה - גר"א'
}

export const POSTER_IMAGES_DIR = 'documents/poster_images'

export const DAYS_OF_THE_WEEK: Array<string> = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];  

export const PARSHA_NAMES = {
"Bereshit": "פרשת בראשית",
"Noach": "פרשת נח",
"Lech-Lecha": "פרשת לך לך",
"Vayera": "פרשת וירא",
"Chayei Sara": "פרשת חיי שרה",
"Toldot": "פרשת תולדות",
"Vayetzei": "פרשת ויצא",
"Vayishlach": "פרשת וישלח",
"Vayeshev": "פרשת וישב",
"Miketz": "פרשת מקץ",
"Vayigash": "פרשת ויגש",
"Vayechi": "פרשת ויחי",
"Shemot": "פרשת שמות",
"Vaera": "פרשת וארא",
"Bo": "פרשת בא",
"Beshalach": "פרשת בשלח",
"Yitro": "פרשת יתרו",
"Mishpatim": "פרשת משפטים",
"Terumah": "פרשת תרומה",
"Tetzaveh": "פרשת תצוה",
"Ki Tisa": "פרשת כי תשא",
"Vayakhel": "פרשת ויקהל",
"Pekudei": "פרשת פקודי",
"Vayikra": "פרשת ויקרא",
"Tzav": "פרשת צו",
"Shemini": "פרשת שמיני",
"Tazria": "פרשת תזריע",
"Metzora": "פרשת מצורע",
"Achrei Mot": "פרשת אחרי מות",
"Kedoshim": "פרשת קדושים",
"Emor": "פרשת אמור",
"Behar": "פרשת בהר",
"Bechukotai": "פרשת בחוקותי",
"Bamidbar": "פרשת במדבר",
"Naso": "פרשת נשא",
"Beha'alotcha": "פרשת בהעלתך",
"Shelach": "פרשת שלח",
"Korach": "פרשת קרח",
"Chukat": "פרשת חקת",
"Balak": "פרשת בלק",
"Pinchas": "פרשת פנחס",
"Matot": "פרשת מטות",
"Masei": "פרשת מסעי",
"Devarim": "פרשת דברים",
"Vaetchanan": "פרשת ואתחנן",
"Eikev": "פרשת עקב",
"Re'eh": "פרשת ראה",
"Shoftim": "פרשת שופטים",
"Ki Teitzei": "פרשת כי תצא",
"Ki Tavo": "פרשת כי תבא",
"Nitzavim": "פרשת נצבים",
"Vayeilech": "פרשת וילך",
"Ha'azinu": "פרשת האזינו",
"V'Zot HaBerachah": "פרשת וזאת הברכה",
"Vezot Haberakhah": "פרשת וזאת הברכה",
"Chanukah Day 7 (on Rosh Chodesh)": "יום ז' דחנוכה",
"Nasso": "פרשת נשא",

// Combined portions
"Vayakhel-Pekudei": "פרשת ויקהל-פקודי",
"Tazria-Metzora": "פרשת תזריע-מצורע",
"Achrei Mot-Kedoshim": "פרשת אחרי מות-קדושים",
"Behar-Bechukotai": "פרשת בהר-בחוקותי",
"Chukat-Balak": "פרשת חקת-בלק",
"Matot-Masei": "פרשת מטות-מסעי",
"Nitzavim-Vayelech": "פרשת נצבים-וילך"
};;


export const ADVANCED_ZMANIM_YIDDISH_MAPPING = {
  "alos72": "עלות השחר (72 דק')",
  "alos90": "עלות השחר (90 דק')",
  "alos60": "עלות השחר (60 דק')",
  "alos120": "עלות השחר (120 דק')",
  "alos16Point1Degrees": "עלות השחר (16.1°)",
  "alos18Degrees": "עלות השחר (18°)",
  "alos19Point8Degrees": "עלות השחר (19.8°)",
  "alos26Degrees": "עלות השחר (26°)",
  "alos60Zmanis": "עלות השחר (זמניות 60)",
  "alos72Zmanis": "עלות השחר (זמניות 72)",
  "alos90Zmanis": "עלות השחר (זמניות 90)",
  "alos96Zmanis": "עלות השחר (זמניות 96)",
  "alos120Zmanis": "עלות השחר (זמניות 120)",
  "misheyakir10Point2Degrees": "משיכיר (10.2°)",
  "misheyakir11Degrees": "משיכיר (11.0°)",
  "misheyakir11Point5Degrees": "משיכיר (11.5°)",
  "misheyakirMinus60": "משיכיר -60 דקות",
  "seaLevelSunrise": "הנץ החמה (מישור)",
  "sunrise": "הנץ החמה (נראה)",
  "sofZmanShmaMGA72Minutes": "סוף זמן ק\"ש (מג\"א 72)",
  "sofZmanShmaMGA90Minutes": "סוף זמן ק\"ש (מג\"א 90)",
  "sofZmanShmaMGA96Minutes": "סוף זמן ק\"ש (מג\"א 96)",
  "sofZmanShmaMGA120Minutes": "סוף זמן ק\"ש (מג\"א 120)",
  "sofZmanShmaMGA16Point1Degrees": "סוף זמן ק\"ש (מג\"א 16.1°)",
  "sofZmanShmaMGA18Degrees": "סוף זמן ק\"ש (מג\"א 18°)",
  "sofZmanShmaMGA19Point8Degrees": "סוף זמן ק\"ש (מג\"א 19.8°)",
  "sofZmanShmaMGA72MinutesZmanis": "סוף זמן ק\"ש (עלות עד צאת 72)",
  "sofZmanShmaAlos16Point1ToTzaisGeonim8Point5Degrees": "סוף זמן ק\"ש (עלות עד צאת 8.5°)",
  "sofZmanShmaGRA": "סוף זמן ק\"ש (גר\"א)",
  "sofZmanTfilaMGA72Minutes": "סוף זמן תפילה (מג\"א 72)",
  "sofZmanTfilaMGA90Minutes": "סוף זמן תפילה (מג\"א 90)",
  "sofZmanTfilaMGA96Minutes": "סוף זמן תפילה (מג\"א 96)",
  "sofZmanTfilaMGA120Minutes": "סוף זמן תפילה (מג\"א 120)",
  "sofZmanTfilaMGA16Point1Degrees": "סוף זמן תפילה (מג\"א 16.1°)",
  "sofZmanTfilaMGA18Degrees": "סוף זמן תפילה (מג\"א 18°)",
  "sofZmanTfilaMGA19Point8Degrees": "סוף זמן תפילה (מג\"א 19.8°)",
  "sofZmanTfilaGRA": "סוף זמן תפילה (גר\"א)",
  "chatzos": "חצות היום",
  "minchaGedola": "מנחה גדולה",
  "minchaGedola30Minutes": "מנחה גדולה (30 דק')",
  "minchaGedolaGreaterThan30": "מנחה גדולה (מג\"א)",
  "minchaKetana": "מנחה קטנה",
  "plagHamincha": "פלג המנחה (גר\"א)",
  "plagHamincha72Minutes": "פלג המנחה (מג\"א 72)",
  "plagHamincha60Minutes": "פלג המנחה (מג\"א 60)",
  "plagHamincha18Degrees": "פלג המנחה (18°)",
  "plagHamincha19Point8Degrees": "פלג המנחה (19.8°)",
  "plagHamincha16Point1Degrees": "פלג המנחה (16.1°)",
  "seaLevelSunset": "שקיעת החמה (מישור)",
  "sunset": "שקיעת החמה (נראה)",
  "tzais18": "צאת הכוכבים (18 דק')",
  "tzais20": "צאת הכוכבים (20 דק')",
  "tzais24": "צאת הכוכבים (24 דק')",
  "tzais30": "צאת הכוכבים (30 דק')",
  "tzais42": "צאת הכוכבים (42 דק')",
  "tzais50": "צאת הכוכבים (50 דק')",
  "tzais60": "צאת הכוכבים (60 דק')",
  "tzais72": "צאת הכוכבים (72 / ר\"ת)",
  "tzais90": "צאת הכוכבים (90 דק')",
  "tzais96": "צאת הכוכבים (96 דק')",
  "tzais120": "צאת הכוכבים (120 דק')",
  "tzaisGeonim3Point65Degrees": "צאת הכוכבים (3.65°)",
  "tzaisGeonim3Point676Degrees": "צאת הכוכבים (3.67°)",
  "tzaisGeonim4Point37Degrees": "צאת הכוכבים (4.37°)",
  "tzaisGeonim4Point61Degrees": "צאת הכוכבים (4.61°)",
  "tzaisGeonim4Point8Degrees": "צאת הכוכבים (4.8°)",
  "tzaisGeonim5Point61Degrees": "צאת הכוכבים (5.61°)",
  "tzaisGeonim5Point95Degrees": "צאת הכוכבים (5.95°)",
  "tzaisGeonim6Point45Degrees": "צאת הכוכבים (6.45°)",
  "tzaisGeonim7Point083Degrees": "צאת הכוכבים (7.08°)",
  "tzaisGeonim7Point67Degrees": "צאת הכוכבים (7.67°)",
  "tzaisGeonim8Point5Degrees": "צאת הכוכבים (8.5°)",
  "tzaisAteretTorah": "צאת הכוכבים (עטרת תורה)",
  "motzeiShabbosLondon": "מוצאי שבת לאנדאן",
  "shaahZmanisGra": "שעה זמנית (גר\"א)",
  "shaahZmanis72Minutes": "שעה זמנית (מג\"א 72)",
  "shaahZmanis90Minutes": "שעה זמנית (מג\"א 90)",
  "shaahZmanis16Point1Degrees": "שעה זמנית (16.1°)",
  "shaahZmanis18Degrees": "שעה זמנית (18°)",
  "shaahZmanis19Point8Degrees": "שעה זמנית (19.8°)",
  "midnightTonight": "חצות הלילה",
  "solarMidnight": "חצות אסטרונומי",
};
export const ADVANCED_ZMANIM_ENGLISH_LABELS = {
  alos72: "Dawn (72 min Fixed)",
  alos90: "Dawn (90 min Fixed)",
  alos60: "Dawn (60 min Fixed)",
  alos120: "Dawn (120 min Fixed)",
  alos16Point1Degrees: "Dawn (16.1 Degrees)",
  alos18Degrees: "Dawn (18 Degrees)",
  alos19Point8Degrees: "Dawn (19.8 Degrees)",
  alos26Degrees: "Dawn (26 Degrees)",
  alos60Zmanis: "Dawn (Zmanis 60 min)",
  alos72Zmanis: "Dawn (Zmanis 72 min)",
  alos90Zmanis: "Dawn (Zmanis 90 min)",
  alos96Zmanis: "Dawn (Zmanis 96 min)",
  alos120Zmanis: "Dawn (Zmanis 120 min)",
  misheyakir10Point2Degrees: "Misheyakir (10.2 Degrees)",
  misheyakir11Degrees: "Misheyakir (11 Degrees)",
  misheyakir11Point5Degrees: "Misheyakir (11.5 Degrees)",
  misheyakirMinus60: "Misheyakir -60 min",
  seaLevelSunrise: "Sunrise (Sea Level)",
  sunrise: "Sunrise (Visible/Elevated)",
  sofZmanShmaMGA72Minutes: "Shema (MGA 72 min)",
  sofZmanShmaMGA90Minutes: "Shema (MGA 90 min)",
  sofZmanShmaMGA96Minutes: "Shema (MGA 96 min)",
  sofZmanShmaMGA120Minutes: "Shema (MGA 120 min)",
  sofZmanShmaMGA16Point1Degrees: "Shema (MGA 16.1 Degrees)",
  sofZmanShmaMGA18Degrees: "Shema (MGA 18 Degrees)",
  sofZmanShmaMGA19Point8Degrees: "Shema (MGA 19.8 Degrees)",
  sofZmanShmaMGA72MinutesZmanis: "Shema (Alos to Tzeis 72 min)",
  sofZmanShmaAlos16Point1ToTzaisGeonim8Point5Degrees: "Shema (Alos to Tzeis 8.5 Degrees)",
  sofZmanShmaGRA: "Shema (GRA)",
  sofZmanTfilaMGA72Minutes: "Tefillah (MGA 72 min)",
  sofZmanTfilaMGA90Minutes: "Tefillah (MGA 90 min)",
  sofZmanTfilaMGA96Minutes: "Tefillah (MGA 96 min)",
  sofZmanTfilaMGA120Minutes: "Tefillah (MGA 120 min)",
  sofZmanTfilaMGA16Point1Degrees: "Tefillah (MGA 16.1 Degrees)",
  sofZmanTfilaMGA18Degrees: "Tefillah (MGA 18 Degrees)",
  sofZmanTfilaMGA19Point8Degrees: "Tefillah (MGA 19.8 Degrees)",
  sofZmanTfilaGRA: "Tefillah (GRA)",
  chatzos: "Midday / Chatzos",
  minchaGedola: "Mincha Gedola (Standard)",
  minchaGedola30Minutes: "Mincha Gedola (30 min)",
  minchaGedolaGreaterThan30: "Mincha Gedola (Greater/MGA)",
  minchaKetana: "Mincha Ketana",
  plagHamincha: "Plag HaMincha (GRA)",
  plagHamincha72Minutes: "Plag HaMincha (MGA 72)",
  plagHamincha60Minutes: "Plag HaMincha (MGA 60)",
  plagHamincha18Degrees: "Plag HaMincha (18 Degrees)",
  plagHamincha19Point8Degrees: "Plag HaMincha (19.8 Degrees)",
  plagHamincha16Point1Degrees: "Plag HaMincha (16.1 Degrees)",
  seaLevelSunset: "Sunset (Sea Level)",
  sunset: "Sunset (Visible/Elevated)",
  tzais18: "Tzeis (18 min)",
  tzais20: "Tzeis (20 min)",
  tzais24: "Tzeis (24 min)",
  tzais30: "Tzeis (30 min)",
  tzais42: "Tzeis (42 min)",
  tzais50: "Tzeis (50 min)",
  tzais60: "Tzeis (60 min)",
  tzais72: "Tzeis (72 min / Rabbeinu Tam)",
  tzais90: "Tzeis (90 min)",
  tzais96: "Tzeis (96 min)",
  tzais120: "Tzeis (120 min)",
  tzaisGeonim3Point65Degrees: "Tzeis (3.65 Degrees)",
  tzaisGeonim3Point676Degrees: "Tzeis (3.67 Degrees)",
  tzaisGeonim4Point37Degrees: "Tzeis (4.37 Degrees)",
  tzaisGeonim4Point61Degrees: "Tzeis (4.61 Degrees)",
  tzaisGeonim4Point8Degrees: "Tzeis (4.8 Degrees)",
  tzaisGeonim5Point61Degrees: "Tzeis (5.61 Degrees)",
  tzaisGeonim5Point95Degrees: "Tzeis (5.95 Degrees)",
  tzaisGeonim6Point45Degrees: "Tzeis (6.45 Degrees)",
  tzaisGeonim7Point083Degrees: "Tzeis (7.08 Degrees)",
  tzaisGeonim7Point67Degrees: "Tzeis (7.67 Degrees)",
  tzaisGeonim8Point5Degrees: "Tzeis (8.5 Degrees)",
  tzaisAteretTorah: "Tzeis Ateret Torah",
  motzeiShabbosLondon: "Motzei Shabbos London",
  shaahZmanisGra: "Hour (GRA)",
  shaahZmanis72Minutes: "Hour (MGA 72)",
  shaahZmanis90Minutes: "Hour (MGA 90)",
  shaahZmanis16Point1Degrees: "Hour (16.1 Degrees)",
  shaahZmanis18Degrees: "Hour (18 Degrees)",
  shaahZmanis19Point8Degrees: "Hour (19.8 Degrees)",
  midnightTonight: "Midnight (Night)",
  solarMidnight: "Solar Midnight",
};



export const ZMANIM_ENGLISH_OFFSETS = {
  sunrise: 0,
  misheyakir: -45,
  misheyakirMachmir: -60,
  sofZmanShma: 180,
  sofZmanShmaMGA: 120,
  sofZmanTfilla: 240,
  sofZmanTfillaMGA: 180,
  chatzot: 360,
  minchaGedola: 390,
  minchaGedolaMGA: 330,
  minchaKetana: 540,
  minchaKetanaMGA: 480,
  plagHaMincha: 615,
  plagMinchaMGA90: 555,
  sunset: 720,
  beinHaShmashos: 735,
  tzeit42min: 762,
  tzeit50min: 770,
  afterSunset60: 780,
  tzeit72min: 792
};


export const BANNER_WEEKDAY_NAMES = ['יום ראשון', 'יום שני', 'יום שלישי', 'יום רביעי', 'יום חמישי']
export const BANNER_EREV_SHABBOS_LABEL = 'ערב שב״ק'
export const BANNER_SHABBOS_LABEL = 'שבת קודש'
export const BANNER_MOTZEI_SHABBOS_LABEL = 'מוצאי שב״ק'
